import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Clock, Trophy, BarChart2, Calculator } from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const MathRaceMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="math-race" // IMPORTANT: Change this for each game!
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "math-race", name: "Math Race" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={MathRaceMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const MathRaceMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // Game states
  const [gameMode, setGameMode] = useState("play"); // "play" | "results"
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Countdown
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Score and metrics
  const [score, setScore] = useState(0);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(20);

  // Tracking correct/incorrect
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);

  // Timestamps for average response time
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);
  const [averageResponseTime, setAverageResponseTime] = useState(0);

  // Current math problem
  const [problem, setProblem] = useState({ question: "", answer: 0 });
  const [answerOptions, setAnswerOptions] = useState([]); // array of possible answers
  const [correctAnswer, setCorrectAnswer] = useState(null);

  // Feedback Overlay
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);

  // Colors for math race game
  const primaryGradient = "from-rose-500 to-pink-600";
  const primaryColorSolid = "bg-rose-500";
  const textAccent = "text-rose-300";

  // Game settings for multiplayer (using standard difficulty)
  const difficultySettings = {
    standard: {
      timeLimit: 60,
      maxOperand: 20,
      timePerQuestion: 4 * 1000,
      distractors: 2,
    },
  };

  // Use a ref to always have the current value of isPlaying
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // ----------------------------------
  //        MULTIPLAYER STATE
  // ----------------------------------
  useEffect(() => {
    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        if (!gameStarted && !showCountdown) {
          // Both players are ready, start the countdown
          startGame();
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        if (!gameStarted) {
          setGameStarted(true);
          startGame();
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        if (isPlaying) {
          endGame();
        }
      }

      // Check if opponent is ready (for UI indication)
      if (gameState.playerReady && opponent) {
        const opponentReady = gameState.playerReady[opponent.email] || false;
        console.log(`Opponent ready: ${opponentReady}`);
      }
    }
  }, [gameState, gameStarted, isPlaying, opponent, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({
        status: "ready",
      });
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // --- Start Game with countdown ---
  const startGame = () => {
    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setResponseTimes([]);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setCurrentRound(0);
    setTimeLeft(difficultySettings.standard.timeLimit);

    // Start the timer countdown
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // IMPORTANT: Make these separate state updates
    setIsPlaying(true);
    setGameStarted(true);

    // Start first round with a guaranteed delay
    setTimeout(() => {
      console.log("Starting first round after countdown");
      startNewRound();
    }, 500);

    // Update the game state
    if (updateGameState) {
      updateGameState({
        status: "active",
        playerReady: true,
        myProgress: 0,
        myScore: 0,
      });
    }
  };

  // Generate a new math problem
  const generateProblem = () => {
    const { maxOperand } = difficultySettings.standard;

    // Randomly pick an operation
    const operations = ["+", "-", "*"];
    const op = operations[Math.floor(Math.random() * operations.length)];

    // Random numbers
    let a = Math.floor(Math.random() * maxOperand) + 1;
    let b = Math.floor(Math.random() * maxOperand) + 1;

    // To keep multiplication from being too big
    if (op === "*") {
      a = Math.floor(Math.random() * (maxOperand / 2)) + 1;
      b = Math.floor(Math.random() * (maxOperand / 2)) + 1;
    }

    let correctAns = 0;
    switch (op) {
      case "+":
        correctAns = a + b;
        break;
      case "-":
        correctAns = a - b;
        break;
      case "*":
        correctAns = a * b;
        break;
      default:
        correctAns = a + b;
        break;
    }

    const question = `${a} ${op} ${b}`;
    return { question, answer: correctAns };
  };

  // Start a new round: generate a problem, distractors, etc.
  const startNewRound = () => {
    if (!isPlayingRef.current || currentRound >= totalRounds) {
      endGame();
      return;
    }

    const newProb = generateProblem();
    setProblem(newProb);

    // Generate distractor answers
    const distractorCount = difficultySettings.standard.distractors;
    const distractors = [];

    // Push random answers that aren't correct
    while (distractors.length < distractorCount) {
      const randAns = newProb.answer + (Math.floor(Math.random() * 6) - 3); // Random offset
      // Prevent duplicates & not equal to correct answer
      if (randAns !== newProb.answer && !distractors.includes(randAns)) {
        distractors.push(randAns);
      }
    }

    const allAnswers = [newProb.answer, ...distractors];
    // Shuffle
    allAnswers.sort(() => Math.random() - 0.5);
    setAnswerOptions(allAnswers);
    setCorrectAnswer(newProb.answer);

    setCurrentRound((prev) => prev + 1);

    // Set up timing
    setResponseStartTime(Date.now());

    // Time-limit each question
    const { timePerQuestion } = difficultySettings.standard;
    setTimeout(() => {
      // If still the same question, user hasn't answered => count it as incorrect
      if (isPlayingRef.current && problem === newProb) {
        handleOptionSelect(null, true);
      }
    }, timePerQuestion);
  };

  // Called when user picks an answer. If "isTimeout" is true, means they didn't pick in time
  const handleOptionSelect = (chosenAns, isTimeout = false) => {
    if (!isPlaying) return;

    const rt = Date.now() - responseStartTime;
    setResponseTimes((prev) => [...prev, rt]);

    let isCorrect = false;
    // If timed out or no selection => automatically wrong
    if (!isTimeout && chosenAns === correctAnswer) {
      isCorrect = true;
    }

    if (isCorrect) {
      setCorrectResponses((prev) => prev + 1);

      // Scoring approach - base points + speed bonus
      let basePoints = 15; // Each correct
      let speedBonus = 1; // Minimal

      if (rt < 250) {
        speedBonus = 10;
      } else if (rt < 350) {
        speedBonus = 8;
      } else if (rt < 500) {
        speedBonus = 6;
      } else if (rt < 1000) {
        speedBonus = 4;
      } else if (rt < 1500) {
        speedBonus = 2;
      } else {
        speedBonus = 1;
      }

      const roundScore = basePoints + speedBonus;
      setScore((prev) => prev + roundScore);

      // Update the game state
      if (updateGameState) {
        updateGameState({
          myProgress: currentRound / totalRounds,
          myScore: score + roundScore,
          myLastResponse: {
            correct: true,
            points: roundScore,
            responseTime: rt,
          },
        });
      }

      setFeedbackCorrect(true);
      setFeedbackMessage("Correct!");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 500);
    } else {
      setIncorrectResponses((prev) => prev + 1);
      // Small penalty
      setScore((prev) => (prev > 0 ? prev - 5 : 0));

      // Update the game state
      if (updateGameState) {
        updateGameState({
          myProgress: currentRound / totalRounds,
          myScore: score > 0 ? score - 5 : 0,
          myLastResponse: {
            correct: false,
            points: -5,
            responseTime: rt,
          },
        });
      }

      setFeedbackCorrect(false);
      setFeedbackMessage(isTimeout ? "Too Slow!" : "Incorrect");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 500);
    }

    // Proceed
    if (currentRound < totalRounds) {
      const delay = 500;
      setTimeout(() => {
        startNewRound();
      }, delay);
    } else {
      setTimeout(() => {
        endGame();
      }, 500);
    }
  };

  // --- End Game ---
  const endGame = () => {
    if (scoreSubmitted) return;
    setScoreSubmitted(true);

    setIsPlaying(false);
    setGameMode("results");
    clearInterval(timerRef.current);

    // Calculate final average response time
    if (responseTimes.length > 0) {
      const avg =
        responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      setAverageResponseTime(Math.round(avg));
    }

    // Submit the score + detailed stats
    if (submitScore) {
      // Create a stats object with all the performance metrics
      const stats = {
        correctResponses,
        incorrectResponses,
        averageResponseTime: Math.round(
          responseTimes.length > 0
            ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
            : 0
        ),
        totalResponses: correctResponses + incorrectResponses,
        gameLength: difficultySettings.standard.timeLimit - timeLeft,
        timestamp: new Date(),
      };

      // Submit both score and stats
      submitScore(score, stats);

      // Also update the game state one last time with final score and progress
      if (updateGameState) {
        updateGameState({
          myProgress: 1, // 100% progress
          myScore: score,
          myLastResponse: {
            gameComplete: true,
            finalStats: stats,
          },
        });
      }
    }
  };

  // Check if the opponent has quit
  useEffect(() => {
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      // If we're still playing, end the game automatically with a win
      if (isPlaying) {
        endGame();
        // You could optionally show a special message:
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email, isPlaying]);

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  return (
    <div className="w-full text-white p-0">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className={`${textAccent} font-medium flex items-center gap-2`}>
            <Calculator size={18} className={textAccent} />
            <span>Math Race</span>
          </div>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          {/* Play Screen */}
          <div className="relative p-6 md:p-8">
            {/* Start button or waiting indicator */}
            {!gameStarted && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                {waitingForOpponent ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 border-4 border-t-transparent border-rose-500 rounded-full animate-spin"></div>
                    <p className="text-white text-xl">
                      Waiting for opponent...
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={handleReadyClick}
                    className="py-3 px-6 bg-gradient-to-r from-rose-500 to-pink-600 hover:brightness-110 text-white rounded-lg"
                  >
                    Ready to Play
                  </button>
                )}
              </div>
            )}

            {/* Countdown overlay */}
            {showCountdown && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                <div className="flex flex-col items-center">
                  <div className="text-6xl font-bold text-white mb-4">
                    {countdown > 0 ? countdown : "GO!"}
                  </div>
                  <div className="text-xl text-slate-300">Get ready...</div>
                </div>
              </div>
            )}

            {/* Game stats header */}
            <div className="flex justify-between items-center mb-6">
              <div className="bg-gradient-to-r from-rose-500/20 to-pink-600/20 px-4 py-2 rounded-lg">
                <span className={`${textAccent} font-medium capitalize`}>
                  Multiplayer Match
                </span>
              </div>

              <div className="flex items-center">
                <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                  <Clock size={18} className="text-slate-400" />
                  <span className="text-white font-mono">
                    {formatTime(timeLeft)}
                  </span>
                </div>

                <div className="ml-4 bg-rose-500/20 px-4 py-2 rounded-lg">
                  <span className="text-white font-medium">Score: {score}</span>
                </div>
              </div>
            </div>

            {/* Opponent progress */}
            {gameState && opponent && (
              <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-slate-300">
                    {opponent.name}'s Progress
                  </span>
                  <span className="text-xs text-white">
                    {gameState?.progress && gameState.progress[opponent.email]
                      ? `${Math.round(
                          gameState.progress[opponent.email] * 100
                        )}%`
                      : "0%"}
                  </span>
                </div>
                <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="bg-rose-500 h-full rounded-full"
                    style={{
                      width: `${
                        ((gameState?.progress &&
                          gameState.progress[opponent.email]) ||
                          0) * 100
                      }%`,
                    }}
                  ></div>
                </div>
                {gameState?.scores &&
                  gameState.scores[opponent.email] !== undefined && (
                    <div className="mt-1 text-right text-xs text-rose-300">
                      Score: {gameState.scores[opponent.email]}
                    </div>
                  )}
              </div>
            )}

            {/* Main Game Content */}
            <div className="flex flex-col items-center justify-center">
              {/* Problem Prompt */}
              <div className="text-center mb-8">
                <h3 className="text-white font-medium mb-2">Solve Quickly!</h3>
                <div className="bg-slate-800 inline-block px-8 py-4 rounded-lg mb-8">
                  <span className="text-3xl font-bold text-rose-300">
                    {problem.question}
                  </span>
                </div>

                {/* Answer Options */}
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  {answerOptions.map((ans, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleOptionSelect(ans, false)}
                      disabled={!isPlaying || showFeedback}
                      className="py-3 px-6 bg-slate-800 hover:bg-slate-700 
                        text-slate-100 rounded-lg transition-colors text-xl font-semibold"
                    >
                      {ans}
                    </button>
                  ))}
                </div>
              </div>

              {/* Game Stats */}
              <div className="mt-4 max-w-lg mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Round</div>
                  <div className="text-white font-medium">
                    {currentRound}/{totalRounds}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Correct</div>
                  <div className="text-green-400 font-medium">
                    {correctResponses}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Errors</div>
                  <div className="text-red-400 font-medium">
                    {incorrectResponses}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Avg. Time</div>
                  <div className="text-white font-medium">
                    {responseTimes.length > 0
                      ? `${Math.round(
                          responseTimes.reduce((a, b) => a + b, 0) /
                            responseTimes.length
                        )} ms`
                      : "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Feedback Overlay */}
            <AnimatePresence>
              {showFeedback && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10"
                >
                  <div className="text-center">
                    <div
                      className={`text-3xl font-bold ${
                        feedbackCorrect ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {feedbackMessage}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You scored {score} points in your multiplayer match!
                </p>
              </div>

              {/* Match results comparison */}
              {gameState && gameState.status === "completed" && opponent && (
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-bold text-white text-center mb-4">
                    Match Results
                  </h3>

                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {user?.firstName || user?.email?.split("@")[0]}
                      </div>
                      <div className="text-3xl font-bold text-rose-300 mt-2">
                        {score}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>

                    <div className="flex flex-col items-center px-4">
                      <div className="text-xl font-bold text-white">VS</div>
                      <div className="w-px h-16 bg-slate-600 my-2"></div>
                    </div>

                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {opponent.name}
                      </div>
                      <div className="text-3xl font-bold text-rose-300 mt-2">
                        {gameState.scores?.[opponent.email] || 0}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {score > (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-2 px-4 rounded-lg font-medium">
                        You Won! 🏆
                      </div>
                    ) : score === (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-amber-500/20 border border-amber-500/30 text-amber-300 py-2 px-4 rounded-lg font-medium">
                        It's a Tie! 🤝
                      </div>
                    ) : (
                      <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 py-2 px-4 rounded-lg font-medium">
                        {opponent.name} Won! Better luck next time!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Performance stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-rose-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-rose-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Errors</span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Response Time</span>
                      <span className="text-white">
                        {averageResponseTime > 0
                          ? `${averageResponseTime} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-rose-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-rose-500 to-pink-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="space-y-3 mt-4">
                      <h4 className="text-white font-medium mb-2">
                        Key Cognitive Skills
                      </h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-rose-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-rose-400" />
                          </div>
                          <span className="text-slate-300 text-sm">
                            Mental math processing speed
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-rose-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-rose-400" />
                          </div>
                          <span className="text-slate-300 text-sm">
                            Performance under time pressure
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-rose-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-rose-400" />
                          </div>
                          <span className="text-slate-300 text-sm">
                            Stress resilience and cognitive endurance
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return to lobby button */}
              <div className="flex justify-center">
                <button
                  onClick={() => submitScore(score)}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Return to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MathRaceMultiWrapper;
